import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import getColor, { getButtonColor, getButtonHover } from "@nishtech/MobiusOverrides/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import InitMobileNav from "@nishtech/Nishtech/InitMobileNav";
import InitOpenClose, { initMobileHeaderNavOpenClose } from "@nishtech/Nishtech/InitOpenClose";
import NishtechZone from "@nishtech/Nishtech/NishtechZone";
import Person from "@nishtech/Store/Nishtech/Icons/Person";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    userName: state.context.session.userName,
    firstName: state.context.session.firstName,
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory &
    HasShellContext;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, false, false, false],
        css: css`
            a {
                background-color: transparent;
                color: ${getColor("primary.main")};
                transition: color 0.35s cubic-bezier(0.3, 0.86, 0.36, 0.95);
                -webkit-text-decoration: none;
                text-decoration: none;

                &:hover {
                    -webkit-text-decoration: none;
                    text-decoration: none;
                    color: #c8102e;
                }
            }

            strong {
                font-weight: bolder;
            }

            img {
                border-style: none;
            }

            button {
                font-family: inherit;
                font-size: 100%;
                line-height: 1.15;
                margin: 0;
                overflow: visible;
                text-transform: none;
                -webkit-appearance: button;
            }

            input {
                font-family: inherit;
                font-size: 100%;
                line-height: 1.15;
                margin: 0;
                overflow: visible;
            }

            [type="submit"] {
                -webkit-appearance: button;
            }

            [type="search"] {
                -webkit-appearance: textfield;
                outline-offset: -2px;
            }

            * {
                box-sizing: inherit;
            }

            &::after {
                box-sizing: inherit;
            }

            &::before {
                box-sizing: inherit;
            }

            ul {
                margin: 0 0 22px;
            }

            input[type="search"] {
                -webkit-appearance: none;
                background-color: transparent;
                border: 1px solid;
                border-radius: 0;
                min-width: 0;
                padding: 1.2rem 2.4rem;
                transition: border-color 0.35s cubic-bezier(0.3, 0.86, 0.36, 0.95);
                width: 100%;

                &:focus {
                    box-shadow: none;
                    outline: none;
                }

                &:hover {
                    box-shadow: none;
                    outline: none;
                }
            }

            .as-header {
                position: relative;
                z-index: 101;

                .as-container {
                    max-width: 1310px;
                }

                .as-user-control {
                    margin: 0 0 0 30px;
                }
            }

            .as-header-area {
                align-items: center;
                display: flex;
                justify-content: flex-end;
            }

            .as-serach-form {
                display: none;
                margin: 0;
                position: relative;

                .as-header-form-group {
                    border: 1px solid ${getColor("secondary.main")};
                    display: flex;
                    width: 277px;
                    padding-left: 0px;
                    border-radius: 35px;
                }

                .as-form-button {
                    align-items: center;
                    background: ${getColor("secondary.main")};
                    border: 0;
                    cursor: pointer;
                    display: flex;
                    height: 32px;
                    justify-content: center;
                    padding: 5px;
                    width: 38px;
                    border-bottom-right-radius: 50%;
                    border-top-right-radius: 50%;
                    margin-right: -1px;

                    svg {
                        height: 17px;
                        width: 17px;
                    }

                    path {
                        transition: fill 0.3s ease;
                    }

                    &:hover {
                        path {
                            fill: #c8102e;
                        }
                    }
                }

                [type="search"] {
                    border: 0;
                    color: rgb(37, 55, 70);
                    flex: 1 1 auto;
                    font-family: "Inter Tight", Arial;
                    font-size: 13px;
                    font-weight: 400;
                    height: 32px;
                    outline: none;
                    padding: 2px 12px;

                    &::placeholder {
                        color: ${getColor("primary.main")};
                        opacity: 1;
                        transition: color 0.3s ease;
                    }

                    &:focus {
                        &::placeholder {
                            color: rgba(37, 55, 70, 0.4);
                            opacity: 1;
                        }
                    }
                }
            }

            .as-topmenu {
                font-size: 13px;
                line-height: 1.2;
                margin: 0 38px;
                list-style: none;
                margin: 0;
                padding: 0;

                li {
                    margin: 0 0 37px;
                }

                a {
                    color: ${getColor("secondary.main")};
                    transition: color 0.3s ease;

                    &:hover {
                        //font-weight: bolder;
                        color: ${getColor("secondary.main")};
                        text-decoration: underline;
                        text-underline-offset: 8px;
                    }
                }

                .as-account-block {
                    li {
                        margin: 0 0 7px;
                    }
                }
            }

            .as-user-control {
                display: flex;
                position: absolute;
                right: 17;
                top: 50%;
                transform: translateY(-50%);
                z-index: 11;
                list-style: none;
                margin: 0;
                padding: 0;

                li {
                    margin: 0 0 0 8px;
                }

                > li {
                    > a {
                        display: block;
                        height: 22px;

                        &:hover {
                            path {
                                fill: ${getColor("primary.main")};
                            }
                        }
                    }
                }

                svg {
                    height: 100%;
                    max-width: 100%;
                    vertical-align: top;
                }

                path {
                    fill: #fff;
                    transition: fill 0.3s ease;
                }

                @media (max-width: 767px) {
                    > li > a:hover path {
                        fill: #fff !important;
                    }
                }
            }

            .as-logo {
                position: relative;
                width: 182px;

                img {
                    vertical-align: top;
                    width: 49%;
                }
            }

            .as-account-block {
                position: relative;

                .as-user-list {
                    background: #fff;
                    border-bottom: 3px solid #c8102e;
                    box-shadow: 0 6px 6px rgba(0, 42, 83, 0.16);
                    font-size: 12px;
                    list-style: none;
                    margin: 5px 0 0;
                    padding: 20px 15px 15px;
                    position: absolute;
                    right: 0;
                    top: 100%;
                    width: 142px;
                    z-index: 111;
                    &:empty {
                        display: none;
                    }
                }

                li {
                    margin: 0 0 7px;
                }

                .as-user-name {
                    color: #c8102e;
                    display: block;
                    font-size: 12px;
                    font-weight: 700;
                    padding: 0;
                }

                a {
                    strong {
                        font-size: 12px;
                        font-weight: 700;
                    }
                }
            }

            .as-header-nav-wrap {
                display: flex;
                padding: 20px 48px 18px;
                position: relative;
                z-index: 1;

                &::before {
                    /* background shorthand chooses:
                       1) --header-bg-composed (if set — supports gradients or url(...))
                       2) otherwise --header-bg-color (if set, simple color)
                       3) otherwise the original default gradient
                    */
                    background: var(
                        --header-bg-composed,
                        var(
                            --header-bg-color,
                            linear-gradient(
                                to right,
                                rgb(201, 230, 238) 10%,
                                ${getColor("secondary.main")} 30%,
                                rgb(4, 35, 89) 70%
                            )
                        )
                    );
                    background-repeat: no-repeat;
                    background-size: var(--header-bg-size, cover);
                    background-position: var(--header-bg-position, center center);
                    bottom: 0;
                    content: "";
                    left: -100vw;
                    position: absolute;
                    right: -100vw;
                    top: 0;
                    z-index: -1;
                }
            }

            .as-navigation {
                // margin: 0 0 0 auto;
                width: 100%;
            }

            .as-nav {
                list-style: none;
                margin: 0;
                padding: 30px 38px 12px;

                > li {
                    margin: 0 0 37px;

                    > a {
                        color: ${getColor("primary.main")};
                        display: block;
                        font-size: 25px;
                        letter-spacing: 0.75px;
                        padding-right: 25px;
                        position: relative;
                        text-transform: uppercase;

                        &:hover {
                            &::after {
                                opacity: 1;
                            }
                        }

                        &::before {
                            border-color: ${getColor("primary.main")};
                            border-style: solid;
                            border-width: 0 2px 2px 0;
                            content: "";
                            height: 14px;
                            position: absolute;
                            right: 7px;
                            top: 9px;
                            transform: rotate(-45deg);
                            transition: border 0.3s ease;
                            width: 14px;
                        }

                        &::after {
                            background: rgb(255, 255, 255);
                            border-radius: 5px;
                            bottom: 0;
                            content: "";
                            display: none;
                            height: 3px;
                            left: 0;
                            opacity: 0;
                            position: absolute;
                            right: 0;
                        }
                    }
                }
            }

            .as-dropdown {
                background: #dce0e4;
                bottom: 0;
                left: 0;
                overflow-y: auto;
                padding: 12px 40px 20px;
                position: fixed;
                top: 86px;
                transform: translateX(100%);
                transition:
                    visibility 0.3s ease,
                    opacity 0.3s ease,
                    transform 0.3s ease;
                width: 100vw;
                z-index: 11;
                &.extra {
                    width: 100%;
                    height: 100px;
                    top: 60% !important;
                    background: transparent;
                }
                .as-menu-title {
                    color: ${getColor("primary.main")};
                    display: block;
                    font-size: 30px;
                    letter-spacing: 0.9px;
                    line-height: 1.2;
                    margin: 0 0 28px;
                }

                .as-menu-sub-title {
                    color: ${getColor("primary.main")};
                    display: block;
                    font-size: 25px;
                    letter-spacing: 0.25px;
                    line-height: 1.2;
                    margin: 0 0 28px;
                    padding: 3px 0 0;
                }

                .as-back-btn {
                    color: ${getColor("primary.main")};
                    display: block;
                    font-size: 23px;
                    font-weight: 700;
                    line-height: 1.2;
                    margin: 0 0 32px;
                    padding: 0 0 0 19px;
                    position: relative;

                    &:hover {
                        color: ${getColor("primary.main")};
                    }

                    &::before {
                        border-color: ${getColor("primary.main")};
                        border-style: solid;
                        border-width: 0 2px 2px 0;
                        content: "";
                        height: 12px;
                        left: 0;
                        position: absolute;
                        top: 7px;
                        transform: rotate(135deg);
                        transition: border 0.3s ease;
                        width: 12px;
                    }

                    &::after {
                        display: none;
                    }
                }
            }

            .as-dropdown-wrap {
                > ul {
                    list-style: none;
                    margin: 0;
                    padding: 0;

                    > li {
                        margin: 0 0 35px;

                        &:hover {
                            > .as-sub-dropdown {
                                opacity: 1;
                                visibility: visible;
                            }
                        }
                    }
                }

                li {
                    > a {
                        color: ${getColor("primary.main")};
                        display: block;
                        font-size: 25px;
                        letter-spacing: 0.25px;
                        line-height: 1.5;
                        position: relative;
                        transition:
                            color 0.3s ease,
                            border 0.3s ease;

                        &:hover {
                            border-color: ${getColor("secondary.main")};
                            color: ${getColor("secondary.main")};

                            &::after {
                                border-color: ${getColor("secondary.main")};
                            }
                        }
                    }
                }

                .as-sub-opener {
                    padding-right: 25px;

                    &::after {
                        border-color: ${getColor("primary.main")};
                        border-style: solid;
                        border-width: 0 2px 2px 0;
                        content: "";
                        height: 14px;
                        position: absolute;
                        right: 0;
                        top: 12px;
                        transform: rotate(-45deg);
                        transition: border 0.3s ease;
                        width: 14px;
                    }
                }

                .as-sub-sub-opener {
                    padding-right: 25px;

                    &::after {
                        border-color: ${getColor("primary.main")};
                        border-style: solid;
                        border-width: 0 2px 2px 0;
                        content: "";
                        height: 14px;
                        position: absolute;
                        right: 0;
                        top: 12px;
                        transform: rotate(-45deg);
                        transition: border 0.3s ease;
                        width: 14px;
                    }
                }
            }

            .as-sub-dropdown {
                background: #dce0e4;
                bottom: 0;
                left: 0;
                padding: 12px 39px 20px;
                position: fixed;
                top: 86px;
                transform: translateX(100%);
                transition:
                    visibility 0.3s ease,
                    opacity 0.3s ease,
                    transform 0.3s ease;
                width: 100vw;
                z-index: 11;

                .as-sub-sub-dropdown {
                    border: 0;
                    opacity: 1;
                    visibility: visible;
                }

                > ul {
                    list-style: none;
                    margin: 0;
                    padding: 0;

                    > li {
                        margin: 0 0 15px;

                        &:hover {
                            > a {
                                border-color: ${getColor("secondary.main")};
                                color: ${getColor("secondary.main")};
                                &::after {
                                    border-color: ${getColor("secondary.main")};
                                }
                            }
                        }
                    }
                }
            }

            .as-sub-sub-dropdown {
                background: #dce0e4;
                bottom: 0;
                left: 0;
                padding: 12px 39px 20px;
                position: fixed;
                top: 86px;
                transform: translateX(100%);
                transition:
                    visibility 0.3s ease,
                    opacity 0.3s ease,
                    transform 0.3s ease;
                width: 100vw;
                z-index: 11;

                > ul {
                    list-style: none;
                    margin: 0;
                    padding: 0;

                    > li {
                        margin: 0 0 15px;
                    }
                }
            }

            .as-call-out-box {
                background: rgba(154, 219, 232, 0.29);
                border-radius: 20px;
                color: #013d54;
                display: block;
                font-size: 20px;
                margin: 0 auto;
                padding: 25px 20px 12px;
                text-align: center;
                transition: box-shadow 0.3s ease;
                width: 307px;

                &:hover {
                    box-shadow: 0 3px 3px rgba(0, 0, 0, 0.16);
                    color: #013d54;

                    .as-text {
                        color: #013d54;

                        span {
                            display: inline;
                        }

                        &::after {
                            border-color: #013d54;
                        }
                    }
                }

                .as-ttl {
                    display: block;
                    font-weight: 700;
                    line-height: 1.25;
                    margin: 0 0 9px;
                }

                .as-text {
                    color: #c8102e;
                    display: inline-block;
                    font-size: 14px;
                    font-weight: 700;
                    line-height: 1.2;
                    padding: 0 13px 0 1px;
                    position: relative;
                    vertical-align: top;

                    span {
                        display: none;
                    }

                    &::after {
                        border-color: ${getColor("secondary.main")};
                        border-style: solid;
                        border-width: 0 2px 2px 0;
                        content: "";
                        height: 7px;
                        position: absolute;
                        right: 0;
                        top: 4px;
                        transform: rotate(-45deg);
                        transition: border 0.3s ease;
                        width: 7px;
                    }
                }
            }

            .as-nav-opener {
                display: block;
                height: 19px;
                left: 3px;
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
                width: 32px;

                span {
                    background: #fff;
                    border-radius: 3px;
                    display: block;
                    height: 1px;
                    left: 0;
                    position: absolute;
                    right: 0;
                    transition:
                        opacity 0.3s ease,
                        transform 0.3s ease,
                        top 0.3s ease;
                    top: 9px;
                }

                &::after {
                    background: #fff;
                    border-radius: 3px;
                    display: block;
                    height: 1px;
                    left: 0;
                    position: absolute;
                    right: 0;
                    transition:
                        opacity 0.3s ease,
                        transform 0.3s ease,
                        top 0.3s ease;
                    content: "";
                    top: 19px;
                }

                &::before {
                    background: #fff;
                    border-radius: 3px;
                    display: block;
                    height: 1px;
                    left: 0;
                    position: absolute;
                    right: 0;
                    transition:
                        opacity 0.3s ease,
                        transform 0.3s ease,
                        top 0.3s ease;
                    content: "";
                    top: 0;
                }
            }

            .as-nav-holder {
                flex: 1;
                padding: 5px 0;
            }

            .as-details {
                background: ${getColor("primary.main")};
                color: #fff;
                margin-top: auto;
                padding: 51px 22px 25px;
                a {
                    color: #fff;
                    span {
                        color: #fff;
                    }
                }
                .as-btn {
                    display: block;
                    margin: 0 auto;
                    max-width: 370px;
                    text-align: center;
                    width: 100%;

                    &:hover {
                        background: #9adbe8;
                        color: ${getColor("primary.main")};
                    }
                }

                .as-details-list {
                    font-family: "Inter Tight", Arial;
                    font-size: 16px;
                    font-weight: 400;
                    list-style: none;
                    margin: 0;
                    padding: 0 18px 27px;

                    li {
                        margin-bottom: 8px;
                        a,
                        button {
                            color: #fff;
                        }
                    }
                }

                .as-details-title {
                    display: block;
                    font-size: 23px;
                    font-weight: 700;
                    line-height: 1.2;
                    padding: 0 18px 16px;

                    a {
                        color: #fff;
                        display: block;
                        position: relative;
                        transition: color 0.3s ease;

                        &::after {
                            border-color: #fff;
                            border-style: solid;
                            border-width: 0 2px 2px 0;
                            content: "";
                            height: 14px;
                            position: absolute;
                            right: 7px;
                            top: 7px;
                            transform: rotate(-45deg);
                            transition: border 0.3s ease;
                            width: 14px;
                        }

                        &:hover {
                            color: ${getColor("secondary.main")};

                            &::after {
                                border-color: ${getColor("secondary.main")};
                            }
                        }
                    }
                }
            }

            .as-nav-form {
                padding: 34px 37px 15px;

                .as-nav-form-wrap {
                    border-bottom: 2px solid ${getColor("primary.main")};
                    display: flex;
                }

                button {
                    align-items: center;
                    background: transparent;
                    border: 0;
                    cursor: pointer;
                    display: flex;
                    height: 32px;
                    justify-content: center;
                    padding: 5px;
                    width: 38px;

                    svg {
                        height: 22px;
                        width: 22px;
                    }

                    path {
                        fill: ${getColor("primary.main")};
                        transition: fill 0.3s ease;
                    }

                    &:hover {
                        path {
                            fill: #c8102e;
                        }
                    }
                }

                [type="search"] {
                    border: 0;
                    color: ${getColor("primary.main")};
                    flex: 1 1 auto;
                    font-family: "Inter Tight", Arial;
                    font-size: 18px;
                    font-weight: 400;
                    height: 36px;
                    letter-spacing: 0.18px;
                    line-height: 1.2;
                    outline: none;
                    padding: 5px 0;

                    &::placeholder {
                        color: ${getColor("primary.main")};
                        opacity: 1;
                        transition: color 0.3s ease;
                    }

                    &:focus {
                        &::placeholder {
                            color: rgba(37, 55, 70, 0.4);
                            opacity: 1;
                        }
                    }
                }
            }

            .as-add-menu {
                border-top: 2px solid #9adbe8;
                list-style: none;
                margin: 0 38px;
                padding: 46px 0 48px;

                li {
                    margin: 0 0 37px;

                    &:last-child {
                        margin-bottom: 0;
                    }
                }

                a {
                    color: ${getColor("primary.main")};
                    display: block;
                    font-size: 25px;
                    letter-spacing: 0.75px;
                    position: relative;
                    text-transform: uppercase;

                    &::before {
                        border-color: ${getColor("primary.main")};
                        border-style: solid;
                        border-width: 0 2px 2px 0;
                        content: "";
                        height: 14px;
                        position: absolute;
                        right: 7px;
                        top: 9px;
                        transform: rotate(-45deg);
                        transition: border 0.3s ease;
                        width: 14px;
                    }
                }
            }

            .as-wrapper {
                overflow: hidden;
                position: relative;
                width: 100%;
            }

            .as-container {
                &::after {
                    clear: both;
                    content: "";
                    display: block;
                }

                margin: 0 auto;
                max-width: 1180px;
                padding-left: 20px;
                padding-right: 20px;
                width: 100%;
            }

            .as-hidden {
                height: 1px;
                left: -9999px;
                overflow: hidden;
                position: absolute;
                top: auto;
                width: 1px;
            }

            .as-btn {
                color: #fff;
                display: inline-block;
                font-size: 16px;
                letter-spacing: 0.48px;
                line-height: 1.2;
                padding: 13px 23px;
                transition:
                    background 0.3s ease,
                    color 0.3s ease;
                vertical-align: top;
            }

            .as-btn-primary {
                background: ${p => resolveColor(p.theme?.button?.primary?.color, p.theme)};
                color: ${p =>
                    p.theme?.button?.primary?.typography?.color || p.theme?.button?.primary?.textColor || "#ffffff"};

                border: ${p => {
                    const type = p.theme?.button?.primary?.buttonType;
                    if (type === "outline") {
                        return `2px solid ${resolveColor(p.theme?.button?.primary?.color, p.theme)}`;
                    }
                    if (type === "ghost") {
                        return "none";
                    }
                    return `2px solid ${resolveColor(p.theme?.button?.primary?.color, p.theme)}`;
                }};

                border-radius: ${p => {
                    switch (p.theme?.button?.primary?.shape) {
                        case "rounded":
                            return "50px";
                        case "square":
                            return "0px";
                        case "pill":
                            return "9999px";
                        default:
                            return "10px";
                    }
                }};

                font-family: ${p => p.theme?.button?.primary?.typography?.fontFamily || "inherit"};
                font-size: ${p => p.theme?.button?.primary?.typography?.size || "inherit"};
                font-weight: ${p =>
                    p.theme?.button?.primary?.typography?.weight ||
                    p.theme?.button?.primary?.typographyProps?.weight ||
                    "normal"};
                line-height: ${p => p.theme?.button?.primary?.typography?.lineHeight || "normal"};
                letter-spacing: ${p => p.theme?.button?.primary?.typography?.letterSpacing || "normal"};
                text-transform: ${p => p.theme?.button?.primary?.typography?.textTransform || "none"};

                ${p => (p.theme?.button?.primary?.typography?.underline ? "text-decoration: underline;" : "")}
                ${p => (p.theme?.button?.primary?.typography?.italic ? "font-style: italic;" : "")}

    ${p => (p.theme?.button?.primary?.shadow ? "box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);" : "")}

    ${p => {
                    const type = p.theme?.button?.primary?.buttonType;
                    const color =
                        p.theme?.button?.primary?.typography?.color || p.theme?.button?.primary?.textColor || "#ffffff";

                    if (type === "outline") {
                        return `
                background: transparent;
                color: ${color};
            `;
                    }

                    if (type === "ghost") {
                        return `
                background: transparent;
                color: ${color};
            `;
                    }

                    return "";
                }}

    ${p => p.theme?.button?.primary?.css}

    &:hover {
                    background: ${p =>
                        resolveColor(
                            p.theme?.button?.primary?.hoverBackgroundColor || p.theme?.button?.primary?.color,
                            p.theme,
                        )};
                    border-color: ${p =>
                        resolveColor(
                            p.theme?.button?.primary?.hoverBackgroundColor || p.theme?.button?.primary?.color,
                            p.theme,
                        )};
                    ${p => p.theme?.button?.primary?.hoverAnimation}
                }
            }

            .as-btn-lg {
                border-radius: 20px;
                font-size: 22px;
                padding: 22px 10px 20px;
            }

            .js-slide-hidden {
                position: absolute !important;
                left: -9999px !important;
                top: -9999px !important;
                display: block !important;
            }
            .as-visible-lg {
                display: none;
            }
            @media (min-width: 1024px) {
                .as-nav-opener {
                    display: none;
                }
                .as-header-area {
                    padding: 12px 6px 12px 0px;
                }

                .as-serach-form {
                    display: block;
                    border: 0px;
                    color: rgb(37, 55, 70);
                    flex: 1 1 auto;
                    font-family: "Inter Tight", Arial;
                    font-size: 13px;
                    font-weight: 400;
                    height: 32px;
                    outline: none;
                    border-top-left-radius: 50px;
                    border-bottom-left-radius: 50px;
                }

                .as-topmenu {
                    display: flex;
                    margin: 0;

                    li {
                        margin: 0 0 0 28px;
                    }
                }

                .as-user-control {
                    position: static;
                    transform: none;

                    li {
                        margin: 0;
                    }

                    > li {
                        > a {
                            height: 17px;
                            width: 21px;
                        }
                    }

                    path {
                        fill: ${getColor("primary.main")};
                    }
                }

                .as-logo {
                    width: 250px;
                    height: 60px;
                }

                .as-account-block {
                    .as-user-list {
                        left: 50%;
                        right: auto;
                        transform: translateX(-50%);
                    }
                }

                .as-header-nav-wrap {
                    padding: 36px 0 17px;
                }

                .as-nav {
                    display: flex;
                    margin: 0 -11px 0 0;
                    padding: 0;

                    > li {
                        margin: 0 0 0 15px;

                        > a {
                            color: #fff;
                            font-size: 16px;
                            letter-spacing: 0.36px;
                            padding: 8px 10px;

                            &::before {
                                display: none;
                            }

                            &::after {
                                display: block;
                            }
                        }
                    }

                    > li.as-hover {
                        > a {
                            &::after {
                                opacity: 1;
                            }
                        }
                    }

                    .as-hover {
                        > .as-dropdown {
                            &.extra {
                                top: 60% !important;
                            }
                            left: 50% !important;
                            opacity: 1;
                            top: 100% !important;
                            visibility: visible;
                        }
                    }
                }

                .as-dropdown {
                    background: #fff;
                    bottom: auto;
                    box-shadow: 0 6px 6px rgba(0, 42, 83, 0.16);
                    left: 50%;
                    opacity: 0;
                    overflow: visible;
                    padding: 0;
                    position: absolute;
                    top: 100%;
                    transform: translateX(-50%);
                    visibility: hidden;

                    .as-back-btn {
                        display: none;
                    }

                    .as-menu-sub-title {
                        display: none;
                    }

                    .as-menu-title {
                        display: none;
                    }
                }

                .as-dropdown-wrap {
                    margin: 0 auto;
                    max-width: 1014px;
                    overflow: auto;
                    padding: 25px 33px 30px;

                    > ul {
                        border-right: 1px solid #9adbe8;
                        padding: 0 30px 0 0;
                        position: relative;
                        width: 307px;

                        > li {
                            margin: 0 0 7px;

                            &:hover {
                                > a {
                                    border-color: ${getColor("secondary.main")};
                                    color: ${getColor("secondary.main")};

                                    &::after {
                                        border-color: ${getColor("secondary.main")};
                                    }
                                }
                            }

                            > a {
                                border-bottom: 2px solid transparent;
                                font-weight: 700;
                            }
                        }
                    }

                    li {
                        > a {
                            font-size: 16px;
                            letter-spacing: 0.16px;
                            line-height: 1.875;
                        }
                    }

                    .as-sub-opener {
                        &::after {
                            height: 7px;
                            right: 9px;
                            width: 7px;
                        }
                    }

                    .as-sub-sub-opener {
                        &::after {
                            height: 7px;
                            right: 9px;
                            width: 7px;
                        }
                    }
                }

                .as-sub-dropdown {
                    background: none;
                    border-right: 1px solid #9adbe8;
                    left: 100%;
                    opacity: 0;
                    padding: 0 29px 0 32px;
                    position: absolute;
                    top: 0;
                    transform: translateX(0);
                    visibility: hidden;
                    width: 338px;

                    &::before {
                        bottom: 0;
                        content: "";
                        position: absolute;
                        right: 100%;
                        top: 0;
                        width: 30px;
                    }

                    > ul {
                        > li {
                            margin: 0 0 9px;

                            &:hover {
                                > a {
                                    border-color: ${getColor("secondary.main")};
                                    color: ${getColor("secondary.main")};
                                    &::after {
                                        border-color: ${getColor("secondary.main")};
                                    }
                                }
                            }
                        }
                    }
                }

                .as-sub-sub-dropdown {
                    background: none;
                    border-right: 1px solid #9adbe8;
                    left: 100%;
                    opacity: 0;
                    padding: 0 29px 0 32px;
                    position: absolute;
                    top: 0;
                    transform: translateX(0);
                    visibility: hidden;
                    width: 338px;

                    &::before {
                        bottom: 0;
                        content: "";
                        position: absolute;
                        right: 100%;
                        top: 0;
                        width: 30px;
                    }

                    > ul {
                        > li {
                            margin: 0 0 9px;

                            &:hover {
                                > a {
                                    border-color: ${getColor("secondary.main")};
                                    color: ${getColor("secondary.main")};

                                    &::after {
                                        border-color: ${getColor("secondary.main")};
                                    }
                                }
                            }
                        }
                    }
                }

                .as-dropdown-wrap-alt {
                    .as-dropdown-wrap {
                        align-items: flex-start;
                        display: flex;
                        padding: 25px 33px 14px;

                        > ul {
                            border: 0;
                            padding: 0 22px 0 0;
                            width: 67.7%;

                            > li {
                                &:hover {
                                    > a {
                                        border-color: transparent;
                                        color: ${getColor("primary.main")};

                                        &::after {
                                            border-color: ${getColor("primary.main")};
                                        }
                                    }

                                    .as-sub-dropdown {
                                        display: block !important;
                                        opacity: 1;
                                        visibility: visible;
                                    }
                                }

                                > a {
                                    &:hover {
                                        border-color: ${getColor("secondary.main")};
                                        color: ${getColor("secondary.main")};

                                        &::after {
                                            border-color: ${getColor("secondary.main")};
                                        }
                                    }
                                }
                            }
                        }

                        .as-sub-dropdown {
                            border: 0;
                            display: block !important;
                            margin: 0 -34px 0 0;
                            opacity: 1;
                            padding: 5px 0 0;
                            position: static !important;
                            visibility: visible;
                            width: auto;

                            ul {
                                display: flex;
                                flex-wrap: wrap;
                                margin: 0 -10px;

                                li {
                                    padding: 0 35px 0 10px;
                                    width: 50%;
                                }
                            }
                        }
                    }
                }

                .as-call-out-box {
                    margin: 12px 0 0;
                }

                .as-topbar-active {
                    .as-nav-drop {
                        height: auto;
                    }
                }

                .as-nav-holder {
                    padding: 0;
                    position: static;
                }

                .as-add-menu {
                    display: none;
                }

                .as-details {
                    display: none;
                }

                .as-hidden-lg {
                    display: none;
                }
                .as-visible-lg {
                    display: contents;
                }

                .as-nav-form {
                    display: none;
                }
            }

            @media (min-width: 1140px) {
                .as-nav {
                    > li {
                        margin: 0 0 0 23px;

                        > a {
                            font-size: 18px;
                            padding: 8px 16px;
                        }
                    }
                }
            }

            @media (max-width: 1023px) {
                .as-nav {
                    .as-selected {
                        > .as-dropdown {
                            transform: none;
                        }
                    }
                }

                .as-dropdown {
                    display: block !important;
                    overflow: hidden;
                    overflow-y: auto;

                    .js-slide-hidden {
                        left: 0 !important;
                        position: fixed !important;
                        top: 86px !important;
                    }
                }
                .as-dropdown.js-slide-hidden {
                    left: 0 !important;
                    position: fixed !important;
                    top: 86px !important;
                }
                .as-sub-dropdown {
                    display: block !important;
                    overflow: hidden;
                    overflow-y: auto;
                }

                .as-sub-sub-dropdown {
                    display: block !important;
                    overflow: hidden;
                    overflow-y: auto;
                }
                .as-nav-active {
                    overflow: hidden;

                    .as-nav-drop {
                        transform: translateX(0);
                    }
                }

                .as-nav-drop {
                    overflow: hidden;
                    background: #dce0e4;
                    bottom: 0;
                    height: calc(var(--full-viewport) - 105px);
                    left: 0;
                    position: fixed;
                    top: 160px;
                    transform: translateX(100%);
                    transition: transform 0.35s cubic-bezier(0.3, 0.86, 0.36, 0.95);
                    width: 100%;

                    &::before {
                        background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='450.873' height='542.785'%3E%3Cpath fill='%23fff' d='M384.918 157.913c87.941 88.068 87.941 230.816 0 318.866-88.087 88.009-230.836 88.009-318.934 0-87.979-88.049-87.979-230.8 0-318.866L222.822 1.195c2.639-2.688 5.24 0 5.24 0l95.246 95.107a3.774 3.774 0 0 1 0 5.376l-156.77 156.774a83.336 83.336 0 1 0 117.865 0l-34.039-34.118c-2.767-2.688 0-5.387 0-5.387l95.067-95.152a3.758 3.758 0 0 1 5.368 0Z' data-name='%D0%9A%D0%BE%D0%BD%D1%82%D1%83%D1%80 230' opacity='.231'/%3E%3C/svg%3E");
                        background-size: 100% 100%;
                        bottom: -16px;
                        content: "";
                        height: 542px;
                        opacity: 0.4;
                        position: absolute;
                        right: -81px;
                        width: 450px;
                        z-index: -1;
                    }
                }

                .as-nav-wrap {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                }

                .as-nav-holder {
                    overflow: hidden;
                    overflow-y: auto;
                }
                .as-dropdown-wrap {
                    > ul {
                        .as-selected {
                            > .as-sub-dropdown {
                                transform: none;
                            }
                        }
                    }
                }
            }

            @media (max-width: 767px) {
                .as-sub-dropdown {
                    > ul {
                        a {
                            font-size: 20px;
                        }
                    }
                }

                .as-sub-sub-dropdown {
                    > ul {
                        a {
                            font-size: 20px;
                        }
                    }
                }
            }

            .as-header-row {
                display: flex;
                align-items: center;
                justify-content: space-between;
                width: 100%;
            }

            /* Row paddings */
            .as-header-top {
                padding: 0.5rem 0;
                border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            }
            .as-header-bottom {
                padding: 0.5rem 0;
                display: flex;
                justify-content: space-between;
                align-items: center;
            }

            /* Shared flex children base */
            .as-header-left,
            .as-header-center,
            .as-header-right {
                display: flex;
                align-items: center;
                gap: 1rem;
                box-sizing: border-box;
            }

            /* Row 1 ratio: 25% / 50% / 25% */
            .as-header-top .as-header-left {
                flex: 0 0 25%;
                max-width: 25%;
                justify-content: flex-start;
            }
            .as-header-top .as-header-center {
                flex: 0 0 65%;
                max-width: 750px;
                justify-content: center;
            }
            .as-header-top .as-header-right {
                flex: 0 0 20%;
                max-width: 20%;
                justify-content: flex-end;
            }

            /* Row 2 ratio: 75% / 25% */
            .as-header-bottom .as-navigation-left {
                flex: 0 0 75%;
                max-width: 75%;
                justify-content: flex-start;
            }
            .as-header-bottom .as-navigation-right {
                flex: 0 0 25%;
                max-width: 25%;
                justify-content: flex-end;
            }

            /* Two-Tier wrapper & background (solid only) */
            .as-header-nav-wrap--twoTier {
                display: flex;
                padding: 20px 48px 18px;
                position: relative;
                z-index: 1;
            }
            .as-header-nav-wrap--twoTier:before {
                content: "";
                position: absolute;
                left: -100vw;
                right: -100vw;
                top: 0;
                bottom: 0;
                z-index: -1;
                background: var(--two-tier-bg-color, #ffffff);
                background-repeat: no-repeat;
                background-size: cover;
                background-position: center center;
                box-sizing: border-box;
            }

            .as-topbar {
                width: 100%;
                box-sizing: border-box;
                display: flex;
                align-items: center;
                justify-content: center;

                /* Values are set dynamically via CSS variables from React */
                background: var(--topbar-bg-color, #ffffff);
                color: var(--topbar-link-color, #000000);
                height: var(--topbar-height, 63px);
            }

            /* TopBar links inherit color */
            .as-topbar a,
            .as-topbar .as-topmenu a {
                color: inherit;
            }

            .as-header-nav-wrap--twoTier .as-header-top .as-header-center .as-serach-form,
            .as-header-nav-wrap--twoTier .as-header-top .as-header-center .as-header-form-group {
                width: 100% !important; /* override classic 277px */
                max-width: 750px; /* cap for large screens */
                box-sizing: border-box;
                display: flex;
                align-items: center;
                background: transparent; /* base fallback */
            }

            .as-header-nav-wrap--twoTier .as-header-top .as-header-center .as-header-form-group {
                padding-left: 0;
                border-radius: 0px;
                border: 1px solid var(--two-tier-search-border-color, #003366) !important;
                align-items: center;
                background: var(--two-tier-search-bg, transparent) !important; /* NEW: pill background */
                box-shadow: none;
            }

            .as-header-nav-wrap--twoTier .as-header-top .as-header-center .as-serach-form {
                background: transparent;
            }

            .as-header-nav-wrap--twoTier .as-header-top .as-header-center .as-header-form-group input[type="search"],
            .as-header-nav-wrap--twoTier .as-header-top .as-header-center .as-header-form-group .as-search-input,
            .as-header-nav-wrap--twoTier .as-header-top .as-header-center .as-header-form-group .as-search-field {
                flex: 1 1 auto;
                min-width: 0;
                border: 0;
                outline: none;
                background: transparent;
                padding: 8px 12px;
                color: var(--twoTierSearchIconColor, #ffffff) !important;
            }

            .as-header-nav-wrap--twoTier .as-header-top .as-header-center .as-header-form-group .as-form-button {
                width: 38px;
                height: 32px;
                margin-right: 0;
                border: 0;
                padding: 5px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: var(--two-tier-search-button-bg, #003366) !important;
                color: var(--two-tier-search-icon-color, #ffffff) !important; /* fallback for icon fonts */
            }

            .as-header-nav-wrap--twoTier .as-header-top .as-header-center input::placeholder {
                color: var(--two-tier-search-placeholder-color, #888888);
                opacity: 1;
            }

            .as-header-nav-wrap--twoTier .as-header-top .as-header-center .as-header-form-group .as-form-button svg,
            .as-header-nav-wrap--twoTier
                .as-header-top
                .as-header-center
                .as-header-form-group
                .as-form-button
                svg
                path,
            .as-header-nav-wrap--twoTier .as-header-top .as-header-center .as-header-form-group .as-form-button svg g {
                fill: var(--two-tier-search-icon-color, #ffffff) !important;
                stroke: var(--two-tier-search-icon-color, #ffffff) !important;
            }

            .as-header-nav-wrap--twoTier .as-header-top .as-header-center .as-header-form-group .as-form-button,
            .as-header-nav-wrap--twoTier .as-header-top .as-header-center .as-header-form-group .as-form-button i,
            .as-header-nav-wrap--twoTier .as-header-top .as-header-center .as-header-form-group .as-form-button span {
                color: var(--two-tier-search-icon-color, #ffffff) !important;
            }

            .as-header-nav-wrap--twoTier .as-header-top .as-header-center .as-header-form-group .as-form-button svg {
                color: var(--two-tier-search-icon-color, #ffffff) !important;
            }

            .as-header-nav-wrap--twoTier .as-header-top ul > li > a,
            .as-header-nav-wrap--twoTier .as-header-bottom .as-navigation-right ul > li > a,
            .as-header-nav-wrap--twoTier .as-user-control a {
                color: var(--two-tier-link-text-color, #003366) !important;
                display: inline-flex;
                align-items: center;
                gap: 0.25rem;
            }

            .as-header-nav-wrap--twoTier .as-header-right .as-user-list li > a > strong {
                color: ${getColor("secondary.main")};
                display: inline-flex;
                align-items: center;
                gap: 0.25rem;
            }
            @media (min-width: 768px) {
                .as-header-nav-wrap--twoTier {
                    .as-card-holder {
                        right: 4%;
                        top: 22%;
                    }
                }
            }
            /* icons inside the same areas */
            .as-header-nav-wrap--twoTier .as-header-top .as-header-right ul > li > a svg,
            .as-header-nav-wrap--twoTier .as-header-top .as-header-right ul > li > a path,
            .as-header-nav-wrap--twoTier .as-header-bottom .as-navigation-right ul > li > a svg,
            .as-header-nav-wrap--twoTier .as-header-bottom .as-navigation-right ul > li > a path,
            .as-header-nav-wrap--twoTier .as-user-control svg,
            .as-header-nav-wrap--twoTier .as-user-control path {
                fill: var(--two-tier-link-text-color, #003366) !important;
                stroke: var(--two-tier-link-text-color, #003366) !important;
            }

            /* hover */
            .as-header-nav-wrap--twoTier .as-header-top .as-header-right ul > li > a:hover,
            .as-header-nav-wrap--twoTier .as-header-bottom .as-navigation-right ul > li > a:hover,
            .as-header-nav-wrap--twoTier .as-user-control a:hover {
                opacity: 0.85;
            }

            @media (max-width: 767px) {
                /* keep pill full width on mobile */
                .as-header-nav-wrap--twoTier .as-header-top .as-header-center .as-header-form-group {
                    max-width: 100%;
                    padding: 0 12px;
                }
                .as-hide-mob {
                    display: none;
                }
            }

            .as-header-nav-wrap--twoTier .as-header-top .as-header-right ul > li > a,
            .as-header-nav-wrap--twoTier .as-header-bottom .as-navigation-right ul > li > a,
            .as-header-nav-wrap--twoTier .as-user-control a {
                color: var(--two-tier-link-text-color, #003366) !important;
                display: inline-flex;
                align-items: center;
                gap: 0.25rem;
            }

            /* icons (svg/path) inside the same areas */
            .as-header-nav-wrap--twoTier .as-header-top .as-header-right ul > li > a svg,
            .as-header-nav-wrap--twoTier .as-header-top .as-header-right ul > li > a path,
            .as-header-nav-wrap--twoTier .as-header-bottom .as-navigation-right ul > li > a svg,
            .as-header-nav-wrap--twoTier .as-header-bottom .as-navigation-right ul > li > a path,
            .as-header-nav-wrap--twoTier .as-user-control svg,
            .as-header-nav-wrap--twoTier .as-user-control path {
                fill: var(--two-tier-link-text-color, #003366) !important;
                stroke: var(--two-tier-link-text-color, #003366) !important;
            }

            /* hover */
            .as-header-nav-wrap--twoTier .as-header-top .as-header-right ul > li > a:hover,
            .as-header-nav-wrap--twoTier .as-header-bottom .as-navigation-right ul > li > a:hover,
            .as-header-nav-wrap--twoTier .as-user-control a:hover {
                opacity: 0.85;
            }

            @media (max-width: 767px) {
                .as-header-top,
                .as-header-bottom {
                    flex-direction: column;
                    align-items: stretch;
                }

                .as-header-top .as-header-left,
                .as-header-top .as-header-center,
                .as-header-top .as-header-right,
                .as-header-bottom .as-navigation-left,
                .as-header-bottom .as-navigation-right {
                    flex: 0 0 100%;
                    max-width: 100%;
                    justify-content: center;
                }

                .as-header-nav-wrap--twoTier .as-header-top .as-header-center .as-header-form-group {
                    max-width: 100%;
                    padding: 0 12px;
                }
            }

            @media (max-width: 767px) {
                .as-header-nav-wrap--twoTier .as-header-top,
                .as-header-nav-wrap--twoTier .as-header-bottom,
                .as-header-nav-wrap--twoTier .as-header-left,
                .as-header-nav-wrap--twoTier .as-header-center,
                .as-header-nav-wrap--twoTier .as-header-right,
                .as-header-nav-wrap--twoTier .as-navigation-left,
                .as-header-nav-wrap--twoTier .as-navigation-right {
                    display: none !important;
                    visibility: hidden !important;
                    opacity: 0 !important;
                    height: 0 !important;
                    overflow: hidden !important;
                    pointer-events: none !important;
                }

                .as-topbar {
                    display: flex !important;
                    visibility: visible !important;
                    opacity: 1 !important;
                }
                .as-topmenu {
                    display: ruby;
                    li {
                        margin: 0px 8px 37px;
                    }
                }
            }

            .as-nav-drop.open {
                transform: translateX(0); /* slides in */
            }

            .as-nav-opener.open {
                &::after {
                    top: 9px;
                    transform: rotate(-45deg);
                }
                &::before {
                    top: 9px;
                    transform: rotate(45deg);
                }
                span {
                    opacity: 0;
                }
            }
            // .header-main-right-Container {
            //    margin-left: auto;
            // }

            // .header-main-center-Container {
            // // flex: 0 0 40%;
            // }

            // .header-main-left-Container {
            // // flex: 0 0 60%;
            // }
            // .as-nav-holder{
            //     display: flex;
            //     align-items: center;
            // }
            @media (min-width: 1024px) {
                .as-nav-holder {
                display: flex;
                align-items: center;      /* vertical center */
                width: 100%;
                }

                .header-main-left-Container {
                flex: 1;
                display: flex;
                align-items: center;
                min-width: max-content;
                }

                .header-main-center-Container {
                flex: 1 1 300px;
                min-width: 250px;
                max-width: 500px;
                display: flex;
                justify-content: flex-start;  /* perfect center */
                align-items: center;
                }
                .header-main-right-Container {
                flex: 0 0 120px;
                display: flex;
                justify-content: flex-end; /* push right */
                align-items: center;
                }
                .as-nav {
                    margin: 0;             /* remove default UL margin */
                    padding: 0;
                    display: flex;         /* if horizontal menu */
                    align-items: center;   /* center li items */
                }
                .as-nav li {
                    display: flex;
                    align-items: center;
                    margin: 0 15px;  /* only horizontal spacing */
                }
                @media (min-width: 1024px) {
                    .as-header-nav-wrap {
                        padding: 5px 0 5px;
                    }
                }
                .as-header-nav-wrap
                {
                    height: 65px;
                    display: flex;
                    align-items: center;
                }
                .logo-imageContainer .as-logo
                {
                    max-width:125px;
                }
                .logo-imageContainer .as-logo img
                {
                    width:100%;
                }
                .as-serach-form .as-header-form-group {
                    width: 100% !important;
                }
                }
                .as-header-area 
                {
                    min-height: 40px;
                }
                @media (min-width: 388px) and (max-width: 850px) {
                    .as-topmenu li {
                        margin: 0 10px !important;
                    }
                }
                @media (max-width: 1024px) {
                    .as-navigation .as-nav-drop.open {
                        top: 77px;
                    }
                }
                @media (max-width: 1024px) {
                    .as-header .as-user-control {
                        top: 75%;
                    }
                    .as-header .as-user-control.hide-for-mobile
                    {
                        display:none;
                    }
                    .logo-imageContainer .as-logo
                    {
                        max-width:125px;
                    }
                    .logo-imageContainer .as-logo img
                    {
                        width:100%;
                    }
                }
                .as-container .as-header-nav-wrap
                {
                    padding-top: 0px;
                    padding-bottom: 0px;
                }
                
        `,
    },
};

const styles = headerStyles;

const getHeaderLayout = (item: HasFields | undefined): string => {
    try {
        const fields = (item as any)?.fields;
        const hv = fields?.headerLayout;
        if (Array.isArray(hv) && hv.length > 0) {
            return (hv[0] && hv[0].fields && hv[0].fields.key) || "classic";
        }
        if (typeof hv === "string") {
            return hv;
        }
    } catch {
        // fallthrough
    }
    return "classic";
};

const Header = (props: Props) => {
    const {
        id,
        siteName,
        session,
        toaster,
        history,
        myAccountPageUrl,
        signInUrl,
        userName,
        firstName,
        dismissRfqUpdates,
        shellContext,
    } = props;

    const { isCurrentPage, isEditing, isInShell } = shellContext;
    const isShell = isCurrentPage && isInShell;
    const isEdit = isCurrentPage && isEditing && isInShell;
    const cmsEditModeClass= isEdit ?"cmsInEditmode" :"";
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const [isMobile, setIsMobile] = useState<boolean>(() => {
        if (typeof window === "undefined" || typeof window.matchMedia === "undefined") return false;
        return window.matchMedia("(max-width: 767px)").matches;
    });

    useEffect(() => {
        let initiated = false;

        const interval = setInterval(() => {
            if (typeof window === "object" && typeof document === "object" && !initiated) {
                try {
                    if (typeof InitOpenClose === "function") InitOpenClose();
                    if (typeof InitMobileNav === "function") InitMobileNav();
                    if (typeof initMobileHeaderNavOpenClose === "function") initMobileHeaderNavOpenClose();

                    const opener = document.querySelector(".as-nav-opener");
                    const drop = document.querySelector(".as-nav-drop");
                    if (opener && drop && !opener.hasAttribute("data-init")) {
                        opener.setAttribute("data-init", "true");
                        opener.addEventListener("click", e => {
                            const current = e.currentTarget as HTMLElement;
                            const container = current.closest(
                                "li.as-nav-item, li.as-sub-nav-item, li.as-sub-sub-nav-item, " +
                                    ".as-nav-item, .as-sub-nav-item, .as-sub-sub-nav-item, nav.as-navigation",
                            );

                            const dropdown = container?.querySelector(
                                ".as-nav-drop, .as-dropdown, .as-sub-dropdown, .as-sub-sub-dropdown",
                            );

                            if (dropdown) {
                                e.preventDefault();
                                // e.stopImmediatePropagation()
                                // e.stopPropagation();  
                                const isOpen = dropdown.classList.contains("open");
                                dropdown.classList.toggle("open");
                                current.setAttribute("aria-expanded", isOpen ? "true" : "false");
                                 // document.body.classList.toggle("as-nav-active");
                                document.body.classList.toggle("as-nav-active",isOpen);
                                opener.classList.toggle("open");
                            }
                        });
                    }
                } catch (err) {
                    console.error("Header init failed:", err);
                }

                initiated = true;
                clearInterval(interval);
            }
        }, 300);

        return () => clearInterval(interval);
    }, [isMobile]);

    useEffect(() => {
        if (getHeaderLayout(props as any) !== "twoTier" || isMobile) return;

        const rootNav = document.querySelector<HTMLElement>(".as-nav");
        if (!rootNav) return;

        const candidates = Array.from(
            rootNav.querySelectorAll<HTMLElement>("li.as-nav-item, li.as-sub-nav-item, li.as-sub-sub-nav-item"),
        );

        const closeAll = () => {
            rootNav.querySelectorAll<HTMLElement>(".as-hover, .is-open").forEach(n => {
                n.classList.remove("as-hover", "is-open");
                n.querySelectorAll<HTMLElement>(".as-dropdown, .as-sub-dropdown, .as-sub-sub-dropdown").forEach(dd => {
                    if (!dd.classList.contains("js-slide-hidden")) dd.classList.add("js-slide-hidden");
                });
                const opener = n.querySelector<HTMLElement>(".as-opener");
                if (opener) opener.setAttribute("aria-expanded", "false");
            });
        };

        const openLi = (li: HTMLElement | null) => {
            if (!li) return;

            const parent = li.parentElement;
            if (parent) {
                parent.querySelectorAll<HTMLElement>(".as-hover, .is-open").forEach(s => {
                    if (s !== li) s.classList.remove("as-hover", "is-open");
                });
            }

            li.classList.add("as-hover", "is-open");

            let dropdownSelector = "";
            if (li.classList.contains("as-nav-item")) {
                dropdownSelector = ":scope > .as-nav-drop, :scope > .as-dropdown";
            } else if (li.classList.contains("as-sub-nav-item")) {
                dropdownSelector = ":scope > .as-sub-dropdown";
            } else if (li.classList.contains("as-sub-sub-nav-item")) {
                dropdownSelector = ":scope > .as-sub-sub-dropdown";
            } else {
                dropdownSelector = ":scope > .as-dropdown, :scope > .as-sub-dropdown, :scope > .as-sub-sub-dropdown";
            }

            let immediateDropdowns: HTMLElement[] = [];
            try {
                immediateDropdowns = Array.from(li.querySelectorAll<HTMLElement>(dropdownSelector));
            } catch (e) {
                immediateDropdowns = Array.from(li.children).filter(
                    (c): c is HTMLElement =>
                        c instanceof HTMLElement &&
                        (c.classList.contains("as-dropdown") ||
                            c.classList.contains("as-sub-dropdown") ||
                            c.classList.contains("as-sub-sub-dropdown") ||
                            c.classList.contains("as-nav-drop")),
                );
            }

            immediateDropdowns.forEach(dd => {
                if (dd.classList.contains("js-slide-hidden")) dd.classList.remove("js-slide-hidden");
                if (dd.style && dd.style.display) dd.style.removeProperty("display");
            });

            const opener = li.querySelector<HTMLElement>(".as-opener");
            if (opener) opener.setAttribute("aria-expanded", "true");
        };

        const findCandidateByPoint = (x: number, y: number): HTMLElement | null => {
            for (const li of candidates) {
                const r = li.getBoundingClientRect();
                const activeZoneBottom = r.top + Math.max(24, r.height * 0.6);
                if (x >= r.left && x <= r.right && y >= r.top && y <= activeZoneBottom) {
                    return li;
                }
            }
            return null;
        };

        let raf: number | null = null;
        const onPointerMove = (ev: MouseEvent) => {
            if (ev.buttons > 0) return;
            if (raf !== null) return;

            const x = ev.clientX;
            const y = ev.clientY;

            raf = window.requestAnimationFrame(() => {
                raf = null;
                const candidate = findCandidateByPoint(x, y);

                if (candidate && !candidate.classList.contains("as-hover")) {
                    openLi(candidate);
                } else if (!candidate) {
                    const elUnder = document.elementFromPoint(x, y);
                    if (!elUnder || !elUnder.closest(".as-nav")) {
                        closeAll();
                    }
                }
            });
        };

        const onMouseLeave = () => closeAll();

        const onLevel1Click = (ev: MouseEvent) => {
            const opener = ev.currentTarget as HTMLAnchorElement | null;
            if (!opener) return;

            if (ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.altKey) return;

            closeAll();
        };

        const captureDocHandler = (ev: Event) => {
            const target = ev.target as Element | null;
            if (!target) return;

            let opener = target.closest?.("a.level1.as-opener") as HTMLAnchorElement | null;
            if (!opener) {
                const p = target.parentElement;
                if (p && p.firstElementChild && (p.firstElementChild as Element).matches?.("a.level1.as-opener")) {
                    opener = p.firstElementChild as HTMLAnchorElement;
                }
            }
            if (!opener) {
                const path = (ev as any).composedPath?.() as Element[] | undefined;
                if (path) {
                    opener =
                        (path.find(
                            n => (n as Element).matches && (n as Element).matches("a.level1.as-opener"),
                        ) as HTMLAnchorElement) || null;
                }
            }
            if (!opener) return;

            const li = opener.closest?.("li.as-nav-item") as HTMLElement | null;
            if (!li) return;

            if (li.classList.contains("as-hover") || li.classList.contains("is-open")) {
                ev.stopPropagation();
                ev.preventDefault();

                const href = opener.getAttribute("href") || opener.href || "";
                const targetAttr = opener.getAttribute("target");
                const relAttr = opener.getAttribute("rel") || "";
                const wantsNewTab =
                    ev instanceof MouseEvent && (ev.ctrlKey || ev.metaKey || ev.shiftKey || targetAttr === "_blank");

                setTimeout(() => {
                    closeAll();
                    if (!href) return;
                    if (wantsNewTab) {
                        const features = undefined;
                        const winTarget = targetAttr || "_blank";
                        window.open(href, winTarget, features);
                    } else {
                        window.location.assign(href);
                    }
                }, 0);
            }
        };

        rootNav.addEventListener("mousemove", onPointerMove, { passive: true });
        rootNav.addEventListener("mouseleave", onMouseLeave);

        const level1Anchors = rootNav.querySelectorAll<HTMLAnchorElement>("li.as-nav-item > a.level1.as-opener");
        level1Anchors.forEach(a => a.addEventListener("click", onLevel1Click));

        document.addEventListener("click", captureDocHandler, true);

        return () => {
            if (raf) window.cancelAnimationFrame(raf);
            rootNav.removeEventListener("mousemove", onPointerMove);
            rootNav.removeEventListener("mouseleave", onMouseLeave);
            level1Anchors.forEach(a => a.removeEventListener("click", onLevel1Click));
            document.removeEventListener("click", captureDocHandler, true);
            closeAll();
        };
    }, [isMobile, props]);

    const pageFields = (props as any).fields || {};

    const rawBg = typeof pageFields.headerBackgroundColor === "string" ? pageFields.headerBackgroundColor.trim() : "";
    const bgColor =
        rawBg === ""
            ? ""
            : rawBg.startsWith("#") || rawBg.startsWith("rgb") || rawBg.startsWith("hsl")
              ? rawBg
              : `#${rawBg}`;

    let bgImageUrl: string | undefined;
    if (pageFields.headerBackgroundImage) {
        if (typeof pageFields.headerBackgroundImage === "string") {
            bgImageUrl = pageFields.headerBackgroundImage;
        } else if (typeof pageFields.headerBackgroundImage === "object") {
            bgImageUrl =
                pageFields.headerBackgroundImage.url ||
                pageFields.headerBackgroundImage.value ||
                pageFields.headerBackgroundImage.src ||
                undefined;
        }
    }

    const gradient =
        typeof pageFields.headerBackgroundGradient === "string" ? pageFields.headerBackgroundGradient.trim() : "";

    const bgSize =
        typeof pageFields.headerBackgroundSize === "string" && pageFields.headerBackgroundSize.trim() !== ""
            ? pageFields.headerBackgroundSize.trim()
            : "cover";
    const bgPosition =
        typeof pageFields.headerBackgroundPosition === "string" && pageFields.headerBackgroundPosition.trim() !== ""
            ? pageFields.headerBackgroundPosition.trim()
            : "center center";

    let composedBgValue: string | undefined;
    if (gradient && bgImageUrl) {
        composedBgValue = `${gradient}, url("${bgImageUrl}")`;
    } else if (gradient) {
        composedBgValue = gradient;
    } else if (bgImageUrl) {
        composedBgValue = `url("${bgImageUrl}")`;
    } else {
        composedBgValue = undefined;
    }

    const normalizeColor = (val: any): string | undefined => {
        const raw = typeof val === "string" ? val.trim() : "";
        if (!raw) return undefined;
        return raw.startsWith("#") || raw.startsWith("rgb") || raw.startsWith("hsl") ? raw : `#${raw}`;
    };

    const normalizeSize = (val: any, fallback = "63px"): string => {
        if (!val) return fallback;
        const raw = val.toString().trim();
        return /^\d+$/.test(raw) ? `${raw}px` : raw;
    };

    // twotier fields
    const topBarBg = normalizeColor(pageFields.topBarBackgroundColor) || "#ffffff";
    const topBarLinkColor = normalizeColor(pageFields.topBarLinkColor) || "#000000";
    const topBarHeight = normalizeSize(pageFields.topBarHeight, "63px");
    const twoTierBg = normalizeColor(pageFields.twoTierBackgroundColor) || "#ffffff";
    const twoTierSearchBorderColor = normalizeColor(pageFields.twoTierSearchBorderColor) || "#003366";
    const twoTierSearchButtonBgColor = normalizeColor(pageFields.twoTierSearchButtonBgColor) || "#003366";
    const twoTierSearchPlaceholderText =
        typeof pageFields.twoTierSearchPlaceholderText === "string"
            ? pageFields.twoTierSearchPlaceholderText.trim() || "Search for everything"
            : "Search for everything";
    const twoTierSearchPlaceholderColor = normalizeColor(pageFields.twoTierSearchPlaceholderColor) || "#888888";
    const twoTierLinkTextColor = normalizeColor(pageFields.twoTierLinkTextColor) || "#003366";
    const twoTierSearchBgColor = normalizeColor(pageFields.twoTierSearchBgColor) || "#ffffff";
    const twoTierSearchIconColor = normalizeColor(pageFields.twoTierSearchIconColor) || "#ffffff";

    const cssVarsStyle: React.CSSProperties = {
        ["--header-bg-composed" as any]: composedBgValue || undefined,
        ["--header-bg-color" as any]: bgColor || undefined,
        ["--header-bg-size" as any]: bgSize,
        ["--header-bg-position" as any]: bgPosition,
        ["--two-tier-bg-color" as any]: twoTierBg,
        ["--two-tier-search-border-color" as any]: twoTierSearchBorderColor,
        ["--two-tier-search-button-bg" as any]: twoTierSearchButtonBgColor,
        ["--two-tier-search-placeholder-color" as any]: twoTierSearchPlaceholderColor,
        ["--two-tier-link-text-color" as any]: twoTierLinkTextColor,
        ["--two-tier-search-bg" as any]: twoTierSearchBgColor,
        ["--two-tier-search-icon-color" as any]: twoTierSearchIconColor,
    };

    const layout = getHeaderLayout(props as any);

    useEffect(() => {
        if (layout !== "twoTier") return;
        const center = document.querySelector(".as-header-nav-wrap--twoTier .as-header-top .as-header-center");
        if (!center) return;
        const input = center.querySelector<HTMLInputElement>('input[type="search"], input[type="text"], input');
        if (input) {
            input.placeholder = twoTierSearchPlaceholderText;
            input.style.setProperty("--two-tier-search-placeholder-color", twoTierSearchPlaceholderColor);
        }
    }, [layout, twoTierSearchPlaceholderText, twoTierSearchPlaceholderColor]);

    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia === "undefined") return;

        const mq = window.matchMedia("(max-width: 1023px)");

        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        if (typeof mq.addEventListener === "function") {
            mq.addEventListener("change", handler);
        } else {
            mq.addListener(handler);
        }
        setIsMobile(mq.matches);
        return () => {
            if (typeof mq.removeEventListener === "function") {
                mq.removeEventListener("change", handler);
            } else {
                mq.removeListener(handler);
            }
        };
    }, []);

    const renderClassicHeader = () => {
        return (
            <>
                <Page as="div" isHeader={true} {...styles.page} style={cssVarsStyle}>
                    <div
                        className="as-topbar"
                        style={{
                            background: topBarBg,
                            height: topBarHeight,
                            color: topBarLinkColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <div className="as-topbar-inner" style={{ width: "100%", color: topBarLinkColor }}>
                            <NishtechZone contentId={id} zoneName="TopBar" hideWrapper={true} />
                        </div>
                    </div>{" "}
                    <Typography as="header" className={"as-header " + cmsEditModeClass}>
                        <div className="as-container">
                            <div className="as-header-area">
                                {/* <NishtechZone contentId={id} zoneName="Search" hideWrapper={true} />
                                <NishtechZone contentId={id} zoneName="QuickLinks" hideWrapper={true} /> */}
                                {/* {isEdit && <NishtechZone contentId={id} zoneName="MyAccountLinks" hideWrapper={true} />}
                                {session.isAuthenticated && (
                                    <ul className="as-visible-lg as-user-control">
                                        <li className="as-account-block">
                                            <a href="#" className="as-opener">
                                                <Person />
                                            </a>
                                            <ul className="as-user-list  js-slide-hidden">
                                                {!isEdit && (
                                                    <NishtechZone
                                                        contentId={id}
                                                        zoneName="MyAccountLinks"
                                                        hideWrapper={true}
                                                    />
                                                )}
                                            </ul>
                                        </li>
                                    </ul>
                                )}
                                <ul className="as-user-control">
                                    <li className="as-hidden-lg as-account-block">
                                        {session.isAuthenticated && (
                                            <a href={myAccountPageUrl}>
                                                <Person />
                                            </a>
                                        )}
                                        {!session.isAuthenticated && (
                                            <a href={signInUrl}>
                                                <Person />
                                            </a>
                                        )}
                                    </li>
                                    <NishtechZone contentId={id} zoneName="Cart" tagNameAs="li" />
                                </ul> */}
                            </div>

                            <div className="as-header-nav-wrap">
                                <NishtechZone
                                    contentId={id}
                                    className="as-logo"
                                    tagNameAs="div"
                                    zoneName="Header Logo"
                                ></NishtechZone>
                                <nav className="as-navigation">
                                    <a href="javascript:void(0)" className="as-nav-opener" aria-expanded="false" aria-haspopup="true">
                                        <span></span>
                                    </a>
                                    <div className="as-nav-drop">
                                        <div className="as-nav-wrap">
                                            {!isEdit && (
                                                <NishtechZone
                                                    contentId={id}
                                                    zoneName="MobileSearch"
                                                    hideWrapper={true}
                                                />
                                            )}
                                            <div className="as-nav-holder">
                                                <NishtechZone
                                                    contentId={id}
                                                    zoneName="MainNavigation"
                                                    hideWrapper={true}
                                                />
                                                <NishtechZone
                                                    zoneName="AdditionalMenu"
                                                    contentId={id}
                                                    hideWrapper={true}
                                                ></NishtechZone>
                                                <div className="as-details">
                                                    <ul className="as-details-list">
                                                        {!isEdit && (
                                                            <NishtechZone
                                                                contentId={id}
                                                                zoneName="MobileMyAccountLinks"
                                                                hideWrapper={true}
                                                            />
                                                        )}
                                                    </ul>
                                                    {!isEdit && (
                                                        <NishtechZone
                                                            zoneName="MobileHeaderRequestProductSamples"
                                                            contentId={id}
                                                            hideWrapper={true}
                                                        ></NishtechZone>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </nav>
                            </div>
                            {isEdit && <NishtechZone contentId={id} zoneName="MobileSearch" hideWrapper={true} />}
                            {isEdit && (
                                <NishtechZone
                                    zoneName="MobileHeaderRequestProductSamples"
                                    contentId={id}
                                    hideWrapper={true}
                                ></NishtechZone>
                            )}
                            {isEdit && (
                                <NishtechZone zoneName="MobileMyAccountLinks" contentId={id} hideWrapper={true} />
                            )}
                        </div>
                    </Typography>
                </Page>
            </>
        );
    };

    return (
        <>
            {layout === "classic" && (
                <Page as="div" isHeader={true} {...styles.page} style={cssVarsStyle}>
                    <NishtechZone contentId={id} zoneName="TopBar" hideWrapper={true} />
                    <Typography as="header" className={"as-header " + cmsEditModeClass}>
                        <div className="as-container">
                            <div className="as-header-area ">
                                {/* <NishtechZone contentId={id} zoneName="Search" hideWrapper={true} /> */}
                                <NishtechZone contentId={id} zoneName="QuickLinks" hideWrapper={true} />
                                {/* {!session.isAuthenticated && (
                                    <ul className="as-visible-lg as-hide-mob ">
                                        <li className="as-account-block">
                                            {!session.isAuthenticated && <a href={signInUrl}>{translate("Login")}</a>}
                                        </li>
                                    </ul>
                                )} */}
                                {/* {isEdit && <NishtechZone contentId={id} zoneName="MyAccountLinks" hideWrapper={true} />}
                                {session.isAuthenticated && (
                                    <ul className="as-visible-lg as-user-control">
                                        <li className="as-account-block">
                                            <a href="#" className="as-opener">
                                                <Person />
                                            </a>
                                            <ul className="as-user-list  js-slide-hidden">
                                                {!isEdit && (
                                                    <NishtechZone
                                                        contentId={id}
                                                        zoneName="MyAccountLinks"
                                                        hideWrapper={true}
                                                    />
                                                )}
                                            </ul>
                                        </li>
                                    </ul>
                                )} */}
                                <ul className="as-user-control">
                                    <li className="as-hidden-lg as-account-block">
                                        {session.isAuthenticated && (
                                            <a href={myAccountPageUrl}>
                                                <Person />
                                            </a>
                                        )}
                                        {!session.isAuthenticated && (
                                            <a href={signInUrl}>
                                                <Person />
                                            </a>
                                        )}
                                    </li>
                                    <NishtechZone contentId={id} zoneName="Cart" tagNameAs="li" className="as-hidden-lg" />
                                </ul>
                            </div>

                            <div className="as-header-nav-wrap">
                                <div className="logo-imageContainer">
                                    <NishtechZone
                                    contentId={id}
                                    className="as-logo"
                                    tagNameAs="div"
                                    zoneName="Header Logo"></NishtechZone>
                                </div>
                                
                                <nav className="as-navigation custom-nav">
                                    <a href="javascript:void(0)" className="as-nav-opener" aria-expanded="false" aria-haspopup="true">
                                        <span></span>
                                    </a>
                                    <div className="as-nav-drop">
                                        <div className="as-nav-wrap">
                                            {!isEdit && (
                                                <NishtechZone
                                                    contentId={id}
                                                    zoneName="MobileSearch"
                                                    hideWrapper={true}
                                                />
                                            )}
                                            <div className="as-nav-holder">
                                                <div className="header-main-left-Container">
                                                    <NishtechZone
                                                    contentId={id}
                                                    zoneName="MainNavigation"
                                                    hideWrapper={true}
                                                />
                                                </div>
                                                <div className="header-main-center-Container">
                                                    <NishtechZone contentId={id} zoneName="Search" hideWrapper={true} />
                                                </div>
                                                <div className="header-main-right-Container">
                                                    <div className="as-header-area ">
                                                        {!session.isAuthenticated && (
                                                            <ul className="as-visible-lg as-hide-mob ">
                                                                <li className="as-account-block">
                                                                    {!session.isAuthenticated && <a href={signInUrl}>{translate("Login")}</a>}
                                                                </li>
                                                            </ul>
                                                        )}
                                                        {isEdit && <NishtechZone contentId={id} zoneName="MyAccountLinks" hideWrapper={true} />}
                                                        {session.isAuthenticated && (
                                                            <ul className="as-visible-lg as-user-control">
                                                                <li className="as-account-block">
                                                                    <a href="#" className="as-opener">
                                                                        <Person />
                                                                    </a>
                                                                    <ul className="as-user-list  js-slide-hidden">
                                                                        {!isEdit && (
                                                                            <NishtechZone
                                                                                contentId={id}
                                                                                zoneName="MyAccountLinks"
                                                                                hideWrapper={true}
                                                                            />
                                                                        )}
                                                                    </ul>
                                                                </li>
                                                            </ul>
                                                        )}
                                                        <ul className="as-user-control hide-for-mobile">
                                                            <li className="as-hidden-lg as-account-block">
                                                                {session.isAuthenticated && (
                                                                    <a href={myAccountPageUrl}>
                                                                        <Person />
                                                                    </a>
                                                                )}
                                                                {!session.isAuthenticated && (
                                                                    <a href={signInUrl}>
                                                                        <Person />
                                                                    </a>
                                                                )}
                                                            </li>
                                                            <NishtechZone contentId={id} zoneName="Cart" tagNameAs="li" />
                                                        </ul>
                                                    </div>
                                                </div>
                                                <NishtechZone
                                                    zoneName="AdditionalMenu"
                                                    contentId={id}
                                                    hideWrapper={true}
                                                ></NishtechZone>
                                                <div className="as-details">
                                                    <ul className="as-details-list">
                                                        {!isEdit && (
                                                            <NishtechZone
                                                                contentId={id}
                                                                zoneName="MobileMyAccountLinks"
                                                                hideWrapper={true}
                                                            />
                                                        )}
                                                    </ul>
                                                    {!isEdit && (
                                                        <NishtechZone
                                                            zoneName="MobileHeaderRequestProductSamples"
                                                            contentId={id}
                                                            hideWrapper={true}
                                                        ></NishtechZone>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </nav>
                            </div>
                            {isEdit && <NishtechZone contentId={id} zoneName="MobileSearch" hideWrapper={true} />}
                            {isEdit && (
                                <NishtechZone
                                    zoneName="MobileHeaderRequestProductSamples"
                                    contentId={id}
                                    hideWrapper={true}
                                ></NishtechZone>
                            )}
                            {isEdit && (
                                <NishtechZone zoneName="MobileMyAccountLinks" contentId={id} hideWrapper={true} />
                            )}
                        </div>
                    </Typography>
                </Page>
            )}

            {layout === "twoTier" &&
                (isMobile ? (
                    renderClassicHeader()
                ) : (
                    <Page as="div" isHeader={true} {...styles.page} style={cssVarsStyle}>
                        <div
                            className="as-topbar"
                            style={{
                                background: topBarBg,
                                height: topBarHeight,
                                color: topBarLinkColor,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <div className="as-topbar-inner" style={{ width: "100%", color: topBarLinkColor }}>
                                <NishtechZone contentId={id} zoneName="TopBar" hideWrapper={true} />
                            </div>
                        </div>
                        <Typography as="header" className={"as-header as-header-nav-wrap as-header-nav-wrap--twoTier " + cmsEditModeClass}>
                            <div className="as-container">
                                <div className="as-header-row as-header-top">
                                    <div className="as-header-left">
                                        <NishtechZone
                                            contentId={id}
                                            className="as-logo"
                                            tagNameAs="div"
                                            zoneName="Header Logo"
                                        />
                                    </div>

                                    <div
                                        className="as-header-center"
                                        data-two-tier-search-placeholder={twoTierSearchPlaceholderText}
                                        style={{
                                            ["--two-tier-search-border-color" as any]: twoTierSearchBorderColor,
                                            ["--two-tier-search-button-bg" as any]: twoTierSearchButtonBgColor,
                                            ["--two-tier-search-placeholder-color" as any]:
                                                twoTierSearchPlaceholderColor,
                                            ["--two-tier-search-bg" as any]: twoTierSearchBgColor, // NEW
                                            ["--two-tier-search-icon-color" as any]: twoTierSearchIconColor, // NEW
                                        }}
                                    >
                                        <NishtechZone contentId={id} zoneName="Search" hideWrapper={true} />
                                    </div>

                                    <div className="as-header-right ">
                                        {!session.isAuthenticated && (
                                            <ul className="as-visible-lg as-user-control">
                                                <li className="as-account-block">
                                                    <a href={signInUrl}>
                                                        <Person />
                                                    </a>
                                                </li>
                                            </ul>
                                        )}

                                        {isEdit && (
                                            <NishtechZone contentId={id} zoneName="MyAccountLinks" hideWrapper={true} />
                                        )}

                                        {session.isAuthenticated && (
                                            <ul className="as-visible-lg as-user-control">
                                                <li className="as-account-block">
                                                    <a href="#" className="as-opener">
                                                        <Person />
                                                    </a>
                                                    <ul className="as-user-list js-slide-hidden">
                                                        {!isEdit && (
                                                            <NishtechZone
                                                                contentId={id}
                                                                zoneName="MyAccountLinks"
                                                                hideWrapper={true}
                                                            />
                                                        )}
                                                    </ul>
                                                </li>
                                            </ul>
                                        )}

                                        <ul className="as-user-control">
                                            <li className="as-hidden-lg as-account-block">
                                                {session.isAuthenticated ? (
                                                    <a href={myAccountPageUrl}>
                                                        <Person />
                                                    </a>
                                                ) : (
                                                    <a href={signInUrl}>
                                                        <Person />
                                                    </a>
                                                )}
                                            </li>
                                            <NishtechZone contentId={id} zoneName="Cart" tagNameAs="li" />
                                        </ul>
                                    </div>
                                </div>

                                <div className="as-header-row as-header-bottom">
                                    <nav className="as-navigation-left">
                                        <NishtechZone contentId={id} zoneName="MainNavigation" hideWrapper={true} />
                                    </nav>

                                    <div className="as-navigation-right">
                                        <NishtechZone zoneName="AdditionalMenu" contentId={id} hideWrapper={true} />
                                    </div>
                                </div>
                            </div>
                        </Typography>
                    </Page>
                ))}
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(withIsInShell(Header)))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
        fieldDefinitions: [
            // Layout selection (Classic / Two Tier)
            {
                name: "headerLayout",
                displayName: "Header Layout",
                editorTemplate: "DropDownField",
                defaultValue: "classic",
                fieldType: "General",
                tab: { displayName: "Layout", sortOrder: 0 },
                options: [
                    { displayName: "Classic", value: "classic" },
                    { displayName: "Two Tier", value: "twoTier" },
                ],
            },

            // === CLASSIC fields (visible when headerLayout === 'classic') ===
            {
                name: "headerBackgroundColor",
                displayName: "Header background color (hex or CSS color)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: { displayName: "Appearance", sortOrder: 0 },
                isVisible: (item: HasFields) => getHeaderLayout(item) === "classic",
            },
            {
                name: "headerBackgroundImage",
                displayName: "Header background image (URL)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: { displayName: "Appearance", sortOrder: 0 },
                isVisible: (item: HasFields) => getHeaderLayout(item) === "classic",
            },
            {
                name: "headerBackgroundGradient",
                displayName: "Header background gradient (CSS)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: { displayName: "Appearance", sortOrder: 0 },
                isVisible: (item: HasFields) => getHeaderLayout(item) === "classic",
            },
            {
                name: "headerBackgroundSize",
                displayName: "Header background size (e.g. cover)",
                editorTemplate: "TextField",
                defaultValue: "cover",
                fieldType: "General",
                tab: { displayName: "Appearance", sortOrder: 1 },
                isVisible: (item: HasFields) => getHeaderLayout(item) === "classic",
            },
            {
                name: "headerBackgroundPosition",
                displayName: "Header background position (e.g. center center)",
                editorTemplate: "TextField",
                defaultValue: "center center",
                fieldType: "General",
                tab: { displayName: "Appearance", sortOrder: 1 },
                isVisible: (item: HasFields) => getHeaderLayout(item) === "classic",
            },

            // === TopBar (shared for both layouts) ===
            {
                name: "topBarBackgroundColor",
                displayName: "Top bar background color (hex or CSS color)",
                editorTemplate: "TextField",
                defaultValue: "#ffffff",
                fieldType: "General",
                tab: { displayName: "Appearance", sortOrder: 0 },
            },
            {
                name: "topBarLinkColor",
                displayName: "Top bar link color (hex or CSS color)",
                editorTemplate: "TextField",
                defaultValue: "#000000",
                fieldType: "General",
                tab: { displayName: "Appearance", sortOrder: 0 },
            },
            {
                name: "topBarHeight",
                displayName: "Top bar height (px)",
                editorTemplate: "TextField",
                defaultValue: "63px",
                fieldType: "General",
                tab: { displayName: "Appearance", sortOrder: 0 },
            },

            // === TWO-TIER fields (visible when headerLayout === 'twoTier') ===
            {
                name: "twoTierBackgroundColor",
                displayName: "Two-Tier Header Background Color (solid color only)",
                editorTemplate: "TextField",
                defaultValue: "#ffffff",
                fieldType: "General",
                tab: { displayName: "Appearance", sortOrder: 0 },
                isVisible: (item: HasFields) => getHeaderLayout(item) === "twoTier",
            },
            {
                name: "twoTierSearchBorderColor",
                displayName: "Two-Tier Search Box Border Color (hex or CSS color)",
                editorTemplate: "TextField",
                defaultValue: "#003366",
                fieldType: "General",
                tab: { displayName: "Appearance", sortOrder: 0 },
                isVisible: (item: HasFields) => getHeaderLayout(item) === "twoTier",
            },
            {
                name: "twoTierSearchButtonBgColor",
                displayName: "Two-Tier Search Icon Background Color (hex or CSS color)",
                editorTemplate: "TextField",
                defaultValue: "#003366",
                fieldType: "General",
                tab: { displayName: "Appearance", sortOrder: 0 },
                isVisible: (item: HasFields) => getHeaderLayout(item) === "twoTier",
            },
            {
                name: "twoTierSearchPlaceholderText",
                displayName: "Two-Tier Search Placeholder Text",
                editorTemplate: "TextField",
                defaultValue: "Search for everything",
                fieldType: "General",
                tab: { displayName: "Appearance", sortOrder: 0 },
                isVisible: (item: HasFields) => getHeaderLayout(item) === "twoTier",
            },
            {
                name: "twoTierSearchPlaceholderColor",
                displayName: "Two-Tier Search Placeholder Text Color (hex or CSS color)",
                editorTemplate: "TextField",
                defaultValue: "#888888",
                fieldType: "General",
                tab: { displayName: "Appearance", sortOrder: 0 },
                isVisible: (item: HasFields) => getHeaderLayout(item) === "twoTier",
            },
            {
                name: "twoTierLinkTextColor",
                displayName: "Two-Tier Link Text Color",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: { displayName: "Appearance", sortOrder: 2 },
                isVisible: (item: HasFields) => getHeaderLayout(item) === "twoTier",
            },
            {
                name: "twoTierSearchBgColor",
                displayName: "Two-Tier Search Box Background Color (hex or CSS color)",
                editorTemplate: "TextField",
                defaultValue: "#ffffff",
                fieldType: "General",
                tab: { displayName: "Appearance", sortOrder: 0 },
                isVisible: (item: HasFields) => getHeaderLayout(item) === "twoTier",
            },
            {
                name: "twoTierSearchIconColor",
                displayName: "Two-Tier Search Icon Color (hex or CSS color)",
                editorTemplate: "TextField",
                defaultValue: "#ffffff",
                fieldType: "General",
                tab: { displayName: "Appearance", sortOrder: 0 },
                isVisible: (item: HasFields) => getHeaderLayout(item) === "twoTier",
            },
        ],
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
